/**
 * YouthLookupCard — Feb-2026 Tyler-Paul sanity check.
 *
 * Lets an admin instantly answer "is youth X on our CYBER caseload?"
 * before they blame the nightly sync. Hits
 * GET /api/performcare/live/youth-lookup which:
 *
 *   - searches the org's already-synced clients (PHI-decrypted server-side)
 *   - splits matches into "in_db" vs "discovered_recently" (last 7d)
 *   - returns a hint pointing to "Sync Everything" when there's no match
 *
 * Mounted in PerformCareSync.js right below the main Sync action card.
 */
import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Search, CheckCircle2, AlertTriangle, Sparkles, RefreshCw } from 'lucide-react';

const YouthLookupCard = ({ apiCall, token }) => {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const runLookup = useCallback(async () => {
    const query = (q || '').trim();
    if (query.length < 2) {
      setError('Type at least 2 characters');
      return;
    }
    setError('');
    setLoading(true);
    setData(null);
    try {
      const res = await apiCall(
        `/performcare/live/youth-lookup?q=${encodeURIComponent(query)}`,
        'GET', null, token,
      );
      const payload = res?.data?.data || res?.data || res;
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  }, [q, apiCall, token]);

  const onKey = (e) => {
    if (e.key === 'Enter') runLookup();
  };

  return (
    <Card data-testid="youth-lookup-card" className="border-2 border-indigo-100">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-600" />
          <CardTitle className="text-lg">Find a youth in CYBER</CardTitle>
        </div>
        <CardDescription>
          Quick check before re-running a sync — type a name (e.g. <em>Tyler Paul</em>)
          and we&apos;ll tell you if they&apos;re already on your caseload.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="youth-lookup-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              placeholder="First name, last name, or both…"
              className="pl-9"
              maxLength={80}
            />
          </div>
          <Button
            data-testid="youth-lookup-submit"
            onClick={runLookup}
            disabled={loading || (q || '').trim().length < 2}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Look up'}
          </Button>
        </div>

        {error && (
          <div
            data-testid="youth-lookup-error"
            className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          >
            {error}
          </div>
        )}

        {data && data.status === 'not_found' && (
          <div
            data-testid="youth-lookup-not-found"
            className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900">
              <div className="font-semibold mb-1">
                No match for &ldquo;{data.query}&rdquo; in your CYBER caseload
              </div>
              <p>{data.hint}</p>
              {data.last_discovery_sync?.completed_at && (
                <p className="mt-2 text-xs text-amber-800">
                  Last discovery sync: {new Date(data.last_discovery_sync.completed_at).toLocaleString()}
                  {data.last_discovery_sync.discovery_summary?.clients_created != null && (
                    <> · {data.last_discovery_sync.discovery_summary.clients_created} new client(s) picked up</>
                  )}
                </p>
              )}
            </div>
          </div>
        )}

        {data && (data.status === 'in_db' || data.status === 'discovered_recently') && (
          <div
            data-testid="youth-lookup-results"
            className="mt-4 space-y-2"
          >
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>
                <strong>{data.match_count}</strong> match{data.match_count === 1 ? '' : 'es'}
                {data.recent_discovery_count > 0 && (
                  <> · <strong>{data.recent_discovery_count}</strong> from a recent sync</>
                )}
              </span>
            </div>
            {data.results.map((r) => (
              <div
                key={r.id}
                data-testid={`youth-lookup-row-${r.id}`}
                className="rounded-lg border border-slate-200 bg-white px-4 py-3 flex items-center justify-between"
              >
                <div>
                  <div className="font-semibold text-slate-900">
                    {r.first_name} {r.last_name}
                    {r.discovered_recently && (
                      <Badge className="ml-2 bg-emerald-100 text-emerald-800 border border-emerald-300">
                        New (last 7d)
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.date_of_birth || '—'}
                    {r.cyber_id && <> · CYBER {r.cyber_id}</>}
                    {r.cyber_status && <> · {r.cyber_status}</>}
                  </div>
                </div>
                <div className="text-xs text-slate-500 text-right">
                  {r.last_seen_in_cyber_at && (
                    <div>Last seen in CYBER: {new Date(r.last_seen_in_cyber_at).toLocaleDateString()}</div>
                  )}
                  {r.discovery_date && (
                    <div>Discovered: {new Date(r.discovery_date).toLocaleDateString()}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default YouthLookupCard;
